import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ArrowLeft, Trash2, RotateCcw, Globe } from "lucide-react";

interface CanvasDetail {
  id: string;
  uuid: string;
  title: string;
  slug: string;
  description: string | null;
  isPublic: boolean;
  thumbnailUrl: string | null;
  nodes: unknown;
  edges: unknown;
  config: unknown;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  nodeCount: number;
  nodeTypeSummary: Record<string, number>;
  user: { id: string; name: string; email: string; image: string | null };
  project: { id: string; name: string; uuid: string } | null;
  pages: Array<{ id: string; uuid: string; title: string; order: number; nodeCount: number; createdAt: string }>;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export const Route = createFileRoute('/dashboard/canvas/$uuid')({
  component: CanvasDetailPage,
})

function CanvasDetailPage() {
  const navigate = useNavigate();
  const { uuid } = Route.useParams();
  const [canvas, setCanvas] = React.useState<CanvasDetail | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => { fetchCanvas(); }, [uuid]);

  async function fetchCanvas() {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas/${uuid}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      const json = await res.json();
      setCanvas(json.data.canvas);
      document.title = `${json.data.canvas.title} - Canvas - Admin`;
    } catch { console.error("Failed to load canvas"); } finally { setLoading(false); }
  }

  async function handleTogglePublic() {
    if (!canvas) return;
    await fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas/${uuid}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ isPublic: !canvas.isPublic }),
    });
    fetchCanvas();
  }

  async function handleSoftDelete() {
    if (!confirm("Soft delete this canvas?")) return;
    await fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas/${uuid}`, { method: "DELETE", credentials: "include" });
    fetchCanvas();
  }

  async function handleRestore() {
    await fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas/${uuid}/restore`, { method: "POST", credentials: "include" });
    fetchCanvas();
  }

  async function handlePermanentDelete() {
    if (!confirm("PERMANENTLY delete this canvas? This cannot be undone.")) return;
    await fetch(`${API_ADMIN_BASE_URL}/api/admin/canvas/${uuid}?permanent=true`, { method: "DELETE", credentials: "include" });
    navigate({ to: "/dashboard/canvas" });
  }

  if (loading) return <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">Loading...</p></div>;
  if (!canvas) return <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">Canvas not found</p></div>;

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="/dashboard/canvas">Canvas</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>{canvas.title}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate({ to: "/dashboard/canvas" })}><ArrowLeft className="h-4 w-4" /></Button>
                <h1 className="text-2xl font-bold">{canvas.title}</h1>
                {canvas.isPublic && <Badge>Public</Badge>}
                {canvas.deletedAt && <Badge variant="destructive">Deleted</Badge>}
              </div>
              <p className="text-muted-foreground text-sm">
                Owner: <span className="text-foreground cursor-pointer hover:underline" onClick={() => navigate({ to: `/dashboard/users/${canvas.user.id}` })}>{canvas.user.email}</span>
                {canvas.project && <> &middot; Project: {canvas.project.name}</>}
                {" \u00b7 "}Created {formatDate(canvas.createdAt)} &middot; Updated {formatDate(canvas.updatedAt)}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={handleTogglePublic}><Globe className="h-3.5 w-3.5 mr-1" />{canvas.isPublic ? "Make Private" : "Make Public"}</Button>
              {canvas.deletedAt ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleRestore}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
                  <Button variant="destructive" size="sm" onClick={handlePermanentDelete}><Trash2 className="h-3.5 w-3.5 mr-1" />Permanent Delete</Button>
                </>
              ) : (
                <Button variant="destructive" size="sm" onClick={handleSoftDelete}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
              )}
            </div>
          </div>
        </div>
      </header>
      <main className="px-4 py-6 md:px-6">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pages">Pages ({canvas.pages.length})</TabsTrigger>
            <TabsTrigger value="raw">Raw Data</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Nodes</span><span className="text-lg font-semibold">{canvas.nodeCount}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Pages</span><span className="text-lg font-semibold">{canvas.pages.length}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Edges</span><span className="text-lg font-semibold">{Array.isArray(canvas.edges) ? (canvas.edges as unknown[]).length : 0}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Public</span><span className="text-lg font-semibold">{canvas.isPublic ? "Yes" : "No"}</span></div>
            </div>
            {Object.keys(canvas.nodeTypeSummary).length > 0 && (
              <div>
                <h3 className="font-medium mb-2">Node Types</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(canvas.nodeTypeSummary).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                    <Badge key={type} variant="outline">{type}: {count}</Badge>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="pages" className="mt-4">
            {canvas.pages.length === 0 ? (<p className="text-muted-foreground">No pages</p>) : (
              <div className="space-y-2">
                {canvas.pages.map((p) => (
                  <div key={p.uuid} className="flex items-center justify-between rounded-lg border p-3">
                    <div><p className="font-medium">{p.title}</p><p className="text-xs text-muted-foreground">Order: {p.order} &middot; {p.nodeCount} nodes &middot; {formatDate(p.createdAt)}</p></div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="raw" className="mt-4">
            <div className="space-y-4">
              <div><h3 className="font-medium mb-1">Nodes ({canvas.nodeCount})</h3><pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[400px]">{JSON.stringify(canvas.nodes, null, 2)}</pre></div>
              <div><h3 className="font-medium mb-1">Edges</h3><pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[400px]">{JSON.stringify(canvas.edges, null, 2)}</pre></div>
              <div><h3 className="font-medium mb-1">Config</h3><pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[400px]">{JSON.stringify(canvas.config, null, 2)}</pre></div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
